import { useCallback, useContext } from 'react';
import { MediaUploadContext } from 'src/pages/media-library/context/media-upload-context';
import { useToast } from 'src/components/ui/use-toast';
import { Logger } from 'src/utils/logger';
import { uploadRetryMessages } from './messages';
import {
  useBackgroundUploadStore,
  PendingUpload,
} from './use-background-upload-store';
import { readPersistedUploads } from './persisted-uploads-storage';

interface UseRetryFailedUploadResult {
  retry: (requestId: string, pendingUpload: PendingUpload) => Promise<void>;
}

/**
 * Retry a failed background upload. Handles two cases:
 *   1. Tus upload itself failed (no mediaId) → resume via tus + IndexedDB cache.
 *   2. Tus succeeded but the attach call failed (mediaId pinned on the
 *      pendingUpload) → reset retry state and let the orchestrator's main-loop
 *      mediaId branch re-issue the attach on its next poll tick.
 *
 * Designed to also work when MediaUploadProvider is *not* in scope — the cell
 * renders from the dashboard / requests-list, where the wizard's provider is a
 * sibling subtree. Case 2 (attach-only) works without the provider because
 * resetUploadForRetry just mutates the store and the orchestrator (running
 * inside the wizard's provider scope) picks up the change on its next poll.
 * Case 1 (tus resume) requires the provider's tus state — outside the
 * provider we surface a toast asking the user to open the request and retry,
 * since we cannot resume a tus upload without the in-memory tus.Upload object.
 */
const useRetryFailedUpload = (): UseRetryFailedUploadResult => {
  const mediaUploadContext = useContext(MediaUploadContext);
  const { toast } = useToast();

  const retry = useCallback(
    async (requestId: string, pendingUpload: PendingUpload) => {
      // Capture resumeUpload once so the type narrowing is locally evident
      // (no need for a non-null assertion later).
      const resumeUpload = mediaUploadContext?.resumeUpload;

      // Pre-flight: a retry is only viable in two shapes — attach-only (mediaId
      // pinned, provider-independent) or full tus resume (needs the
      // MediaUploadProvider in scope). On the dashboard / requests-list the
      // provider is in a sibling subtree, so for full-retry candidates the
      // resume path can't run. Bail *before* resetUploadForRetry so we don't
      // burn one of the three userRetryCount slots on a click that never
      // attempts an actual retry.
      if (!pendingUpload.mediaId && !resumeUpload) {
        Logger.warn(
          'Retry attempted outside MediaUploadProvider scope',
          'useRetryFailedUpload',
          { requestId, tempId: pendingUpload.tempId },
        );
        toast({
          variant: 'destructive',
          description: uploadRetryMessages.retryProviderUnavailable,
        });
        return;
      }

      // Second pre-flight, same reason as the first: bail *before* resetUploadForRetry so a click
      // that cannot attempt anything does not burn one of the three userRetryCount slots. A resume
      // is refused while another trigger already owns the record (the orchestrator's mount effect,
      // or the media-library Resume button). That refusal is deliberately silent — it fires neither
      // callback — so without this check the slot was spent on a click that never ran, and three of
      // them disabled Retry for good behind "Retry limit reached — re-submit the request".
      //
      // Nothing to resolve here: the attempt that owns the record drives it to success or to a real
      // failure, and the orchestrator's poll reflects that onto this pendingUpload.
      if (
        !pendingUpload.mediaId &&
        mediaUploadContext?.isResumeInFlight?.(pendingUpload.tempId)
      ) {
        Logger.info(
          'Retry skipped — a resume is already in flight for this record',
          'useRetryFailedUpload',
          { requestId, tempId: pendingUpload.tempId },
        );
        // Informational, not destructive: bailing without a word is the same dead-click the
        // media-library Resume button had. The row is not stranded — the owning attempt resolves it.
        toast({
          variant: 'info',
          description: uploadRetryMessages.retryAlreadyInFlight,
        });
        return;
      }

      const reset = useBackgroundUploadStore
        .getState()
        .resetUploadForRetry(requestId, pendingUpload.tempId);
      if (!reset) {
        // User-retry cap reached — store mutation refused. The cell surfaces
        // its own toast; bail without further work to avoid re-issuing the
        // attach call against the cap.
        Logger.info(
          'Retry skipped — user retry cap reached',
          'useRetryFailedUpload',
          { requestId, tempId: pendingUpload.tempId },
        );
        return;
      }

      // Attach-only retry: mediaId is the unambiguous identifier for this
      // upload's media object. The orchestrator's main-loop mediaId branch
      // picks it up from the (preserved) pendingUpload and re-attaches.
      if (pendingUpload.mediaId) return;

      // Logically unreachable — the pre-flight returned when both
      // !pendingUpload.mediaId and !resumeUpload — but TS can't narrow across
      // the early-return + mediaId branch, so this explicit guard avoids a
      // non-null assertion on resumeUpload below. Logged as an error so a
      // future refactor that breaks the invariant lands in Sentry instead of
      // silently swallowing the user's click.
      if (!resumeUpload) {
        Logger.error(
          'Unreachable: resumeUpload missing past pre-flight in retry hook',
          'useRetryFailedUpload',
          { requestId, tempId: pendingUpload.tempId },
        );
        return;
      }

      // Full retry: tus upload failed — kick it off again via resumeUpload.
      // resumeUpload looks up the original file in IndexedDB by id. Match
      // persistedUpload by id (= pendingUpload.tempId — both come from the
      // same urlDetails.id), not by fileName + fileSize, so two requests
      // with same-name same-size files don't cross-resume each other.
      // localStorage is read directly so the hook works even if the provider's
      // React state is one render behind (or absent).
      const persisted = readPersistedUploads().find((p) => p.id === pendingUpload.tempId);

      if (!persisted) {
        Logger.warn(
          'Retry requested but no persisted upload record was found',
          'useRetryFailedUpload',
          { requestId, tempId: pendingUpload.tempId, fileName: pendingUpload.fileName },
        );
        useBackgroundUploadStore
          .getState()
          .markUploadFailed(
            requestId,
            pendingUpload.tempId,
            uploadRetryMessages.retryUnavailable,
          );
        toast({
          variant: 'destructive',
          description: uploadRetryMessages.retryFailedForFile(pendingUpload.fileName),
        });
        return;
      }

      try {
        const outcome = await resumeUpload({
          persistedUpload: persisted,
          onSuccess: () => {
            Logger.info(
              `Retry upload completed: ${pendingUpload.fileName}`,
              'useRetryFailedUpload',
            );
          },
          onError: (error) => {
            const message =
              error?.name === 'FILE_NOT_IN_CACHE'
                ? uploadRetryMessages.fileNotInCache
                : error?.message || 'Retry failed';
            Logger.error(
              `Retry upload failed: ${pendingUpload.fileName}`,
              'useRetryFailedUpload',
              { requestId, error },
            );
            useBackgroundUploadStore
              .getState()
              .markUploadFailed(requestId, pendingUpload.tempId, message);
            toast({
              variant: 'destructive',
              description: uploadRetryMessages.retryToastFailure(pendingUpload.fileName, message),
            });
          },
        });

        // Residual race the pre-flight above cannot close: another trigger took the record between
        // the check and the acquire. The slot is already spent and there is nothing to refund, but
        // the row is not stranded — the owning attempt resolves it. Logged so this stays visible if
        // it turns out to be common.
        if (outcome === 'refused') {
          Logger.info(
            'Retry resume refused — another attempt took the record first',
            'useRetryFailedUpload',
            { requestId, tempId: pendingUpload.tempId },
          );
        }
      } catch (err) {
        Logger.error('Failed to start retry', 'useRetryFailedUpload', { err });
        useBackgroundUploadStore
          .getState()
          .markUploadFailed(requestId, pendingUpload.tempId, uploadRetryMessages.retryFailedToStart);
      }
    },
    [mediaUploadContext, toast],
  );

  return { retry };
};

export { useRetryFailedUpload };
